// src/pages/OwnerSecurity.jsx
import React, { useEffect, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useUser } from "../context/UserContext";
import {
  ownerSetPasswordApi,
  fetchOwnerProfile,
} from "../api/ownerPortalApi";

export default function OwnerSecurity() {
  const { isOwner, isAuthenticated } = useUser();

  const [pwd1, setPwd1] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [busy, setBusy] = useState(false);

  const [loadingProfile, setLoadingProfile] = useState(true);
  const [hasPassword, setHasPassword] = useState(false);

  // Load owner profile to detect if password is already set
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!isAuthenticated || !isOwner) {
        setLoadingProfile(false);
        return;
      }
      try {
        const data = await fetchOwnerProfile();
        if (!mounted) return;
        // Expect backend to include hasPassword: boolean
        setHasPassword(Boolean(data?.hasPassword));
      } catch (err) {
        console.error("Failed to load owner profile for password status", err);
        // If we can't detect, default to "no password set" (clean UX)
        if (mounted) setHasPassword(false);
      } finally {
        if (mounted) setLoadingProfile(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [isAuthenticated, isOwner]);

  const trimmed1 = pwd1.trim();
  const trimmed2 = pwd2.trim();
  const tooShort =
    (!!trimmed1 || !!trimmed2) &&
    (trimmed1.length < 6 || trimmed2.length < 6);
  const mismatch = trimmed1 && trimmed2 && trimmed1 !== trimmed2;

  const canSave =
    trimmed1.length >= 6 &&
    trimmed2.length >= 6 &&
    trimmed1 === trimmed2 &&
    !busy;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!canSave) {
      setError("Passwords must match and be at least 6 characters.");
      return;
    }

    try {
      setBusy(true);
      // 🔑 Same as mobile: use existing JWT on axios instance
      await ownerSetPasswordApi(trimmed1);
      setSuccess(
        hasPassword
          ? "Password updated successfully."
          : "Password set successfully. You can now sign in with phone + password."
      );
      setPwd1("");
      setPwd2("");
      setHasPassword(true);
    } catch (err) {
      console.error("Owner password set failed", err);
      setError("Could not update password. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  // Guard when hit directly without auth
  if (!isAuthenticated || !isOwner) {
    return (
      <div className="min-h-screen bg-white text-slate-900">
        <Header />
        <main className="mx-auto max-w-xl px-4 sm:px-6 xl:px-8 py-16">
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-8 text-sm text-slate-700">
            Please sign in as a vehicle owner to manage your owner password.
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const title = hasPassword
    ? "Change owner password"
    : "Set owner password";
  const subtitle = hasPassword
    ? "Update the password for your Wayroot owner login. OTP login will continue to work as a backup."
    : "Create a password for your Wayroot owner login. You can continue using OTP as well.";
  const buttonLabel = hasPassword ? "Update password" : "Set password";

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Header />
      <main className="mx-auto max-w-xl px-4 sm:px-6 xl:px-8 py-12">
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold">{title}</h1>
              <p className="mt-1 text-sm text-slate-600">{subtitle}</p>
            </div>
            {loadingProfile && (
              <span className="text-[11px] text-slate-400">
                Checking status…
              </span>
            )}
          </div>

          {error && (
            <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-700">
              {error}
            </div>
          )}
          {success && (
            <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-xs text-emerald-700">
              {success}
            </div>
          )}

          <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
            <label className="block">
              <span className="text-sm text-slate-600">New password</span>
              <input
                type="password"
                value={pwd1}
                onChange={(e) => {
                  setPwd1(e.target.value);
                  setError("");
                  setSuccess("");
                }}
                autoComplete="new-password"
                placeholder="At least 6 characters"
                className={`mt-1.5 w-full rounded-xl border px-3 py-2.5 text-[15px] outline-none focus:ring-2 focus:ring-slate-900/10 ${
                  tooShort
                    ? "border-rose-300 bg-rose-50/40"
                    : "border-slate-300 bg-white"
                }`}
              />
            </label>

            <label className="block">
              <span className="text-sm text-slate-600">Confirm password</span>
              <input
                type="password"
                value={pwd2}
                onChange={(e) => {
                  setPwd2(e.target.value);
                  setError("");
                  setSuccess("");
                }}
                autoComplete="new-password"
                placeholder="Repeat new password"
                className={`mt-1.5 w-full rounded-xl border px-3 py-2.5 text-[15px] outline-none focus:ring-2 focus:ring-slate-900/10 ${
                  mismatch
                    ? "border-rose-300 bg-rose-50/40"
                    : "border-slate-300 bg-white"
                }`}
              />
            </label>

            {(tooShort || mismatch) && !error && (
              <p className="text-xs text-rose-600">
                {tooShort
                  ? "Password should be at least 6 characters."
                  : "Passwords do not match."}
              </p>
            )}

            <button
              type="submit"
              disabled={!canSave}
              className="w-full inline-flex items-center justify-center rounded-xl bg-black px-5 py-2.5 text-white font-medium hover:opacity-90 active:opacity-80 disabled:opacity-60"
            >
              {busy ? "Saving..." : buttonLabel}
            </button>

            <p className="mt-3 text-xs text-slate-500">
              This password is only for your <strong>owner login</strong> on
              Wayroot. It does not change any user (rider) account password.
              You can always continue logging in with OTP as well.
            </p>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
}
